import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getCloudbaseAuth, getCloudbaseTokens, refreshCloudbaseSession } from '../lib/cloudbase'
import {
  ACCESS_TOKEN_EXPIRES_AT_KEY,
  ACCESS_TOKEN_KEY,
  AUTH_TOKENS_UPDATED_EVENT,
  REFRESH_TOKEN_KEY,
  clearStoredTokens,
  getStoredTokens,
  setStoredTokens,
} from '../lib/authTokens'
import { apiRequest, assertOk } from '../lib/apiClient'

export type UserMe = {
  nick_name?: string
  avatar_url?: string
  phone?: string
  email?: string
  type?: string
  credits?: number
  master_ring?: string
}

export type ProviderProfile = {
  provider_id: string
  sub?: string
  name?: string
  picture?: string
  phone_number?: string
  meta?: any
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>(getStoredTokens().accessToken)
  const refreshToken = ref<string>(getStoredTokens().refreshToken)
  const tokenExpiresAt = ref<number | null>(getStoredTokens().accessTokenExpiresAt || null)
  const me = ref<UserMe | null>(null)
  const providers = ref<ProviderProfile[]>([])
  const isLoggedIn = computed(() => !!token.value)
  const wxOpenProfile = computed(() => {
    const hit = providers.value.find((p) => {
      const id = String(p?.provider_id || '').trim()
      return id === 'wx_open' || id === 'wx'
    })
    return hit || null
  })
  const WX_OPEN_PENDING_PROVIDER_TOKEN_KEY = 'wx_open_pending_provider_token'

  function syncFromStorage() {
    const t = getStoredTokens()
    token.value = String(t.accessToken || '').trim()
    refreshToken.value = String(t.refreshToken || '').trim()
    tokenExpiresAt.value = typeof t.accessTokenExpiresAt === 'number' ? t.accessTokenExpiresAt : null
    if (!token.value) {
      me.value = null
      providers.value = []
    }
  }

  function normalizeAvatarValue(v: string) {
    const raw = String(v || '').trim()
    if (!raw) return ''
    const baseRaw = (import.meta.env.BASE_URL as string | undefined) || '/'
    const base = baseRaw === '/' ? '/' : baseRaw.replace(/\/?$/, '/')
    const withBase = (p: string) => {
      const s = String(p || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      const rel = s.replace(/^\//, '')
      return `${base}${rel}`
    }
    if (!/^https?:\/\//i.test(raw)) {
      if (raw.startsWith('/assets/')) return withBase(raw)
      if (raw.startsWith('/') && raw.includes('/assets/')) return raw
      if (base !== '/' && raw.startsWith(base)) return raw
      return raw
    }
    try {
      const u = new URL(raw)
      const p = u.pathname || ''
      if (p.startsWith('/assets/')) return withBase(p)
      if (p.startsWith('/') && p.includes('/assets/')) return p
      if (base !== '/' && p.startsWith(base)) return p
      return raw
    } catch {
      return raw
    }
  }

  if (typeof window !== 'undefined') {
    try {
      window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, ((e: any) => {
        token.value = String(e?.detail?.accessToken || '').trim()
        refreshToken.value = String(e?.detail?.refreshToken || '').trim()
        tokenExpiresAt.value = Number.isFinite(Number(e?.detail?.accessTokenExpiresAt)) ? Number(e?.detail?.accessTokenExpiresAt) : null
        if (!token.value) me.value = null
      }) as any)
    } catch {}
    try {
      window.addEventListener('storage', (ev) => {
        const k = String(ev?.key || '')
        if (k !== ACCESS_TOKEN_KEY && k !== REFRESH_TOKEN_KEY && k !== ACCESS_TOKEN_EXPIRES_AT_KEY) return
        syncFromStorage()
      })
    } catch {}
  }

  function setToken(next: string) {
    setStoredTokens({ accessToken: next || '' })
  }

  function clearToken() {
    clearStoredTokens()
    token.value = ''
    refreshToken.value = ''
    tokenExpiresAt.value = null
    me.value = null
    providers.value = []
  }

  async function fetchProviders() {
    if (!token.value) throw new Error('未登录')
    const auth = await getCloudbaseAuth()
    const raw = await (auth as any).getProviders()
    let list: any[] = []
    if (Array.isArray(raw)) list = raw
    else if (Array.isArray((raw as any)?.providers)) list = (raw as any).providers
    else if (Array.isArray((raw as any)?.data?.providers)) list = (raw as any).data.providers
    else if (Array.isArray((raw as any)?.data)) list = (raw as any).data
    providers.value = list
      .map((x) => (x && typeof x === 'object' ? x : null))
      .filter(Boolean)
      .filter((x: any) => x.bind === true)
      .map((x: any) => ({
        provider_id: String(x.provider_id || x.providerId || x.id || '').trim(),
        sub: x.sub ? String(x.sub) : undefined,
        name: x.provider_user_name ? String(x.provider_user_name) : undefined,
        picture: x.picture ? String(x.picture) : x.provider_avatar_url ? String(x.provider_avatar_url) : undefined,
        phone_number: x.phone_number ? String(x.phone_number) : undefined,
        meta: x.meta,
      }))
      .filter((x) => x.provider_id)
    return providers.value
  }

  async function fetchMe() {
    if (!token.value) throw new Error('未登录')
    const resp = await apiRequest<UserMe>('/user/me', { method: 'GET', token: token.value })
    const data = assertOk(resp, '获取用户信息失败')
    let merged: any = data
    try {
      const cbAuth = await getCloudbaseAuth()
      const u = await (cbAuth as any).getUser()
      const user = (u as any)?.data?.user
      const meta = (user as any)?.user_metadata
      const cbEmail = String((user as any)?.email || '').trim()
      const cbNick = String(meta?.nickName || '').trim()
      const cbAvatar = normalizeAvatarValue(String(meta?.avatarUrl || meta?.picture || '').trim())
      if (cbNick || cbAvatar || cbEmail) {
        merged = {
          ...(data as any),
          ...(cbNick ? { nick_name: cbNick } : null),
          ...(cbAvatar ? { avatar_url: cbAvatar } : null),
          ...(!String((data as any)?.email || '').trim() && cbEmail ? { email: cbEmail } : null),
        }
      }
    } catch {}
    if ((merged as any)?.avatar_url) (merged as any).avatar_url = normalizeAvatarValue(String((merged as any).avatar_url || ''))
    me.value = merged
    try {
      await fetchProviders()
    } catch {}
    return merged
  }

  async function syncTokenFromCloudbase() {
    const t: any = await getCloudbaseTokens()
    const expiresAt =
      typeof t?.expiresAt === 'number' && Number.isFinite(t.expiresAt)
        ? Math.round(t.expiresAt)
        : typeof t?.expiresIn === 'number' && Number.isFinite(t.expiresIn)
          ? Date.now() + Math.max(0, Math.round(t.expiresIn * 1000))
          : undefined
    setStoredTokens({ accessToken: t.accessToken, refreshToken: t.refreshToken || '', accessTokenExpiresAt: expiresAt })
    await fetchMe()
    try {
      await fetchProviders()
    } catch {}
    return t.accessToken
  }

  async function refreshAccessToken() {
    try {
      const next: any = await refreshCloudbaseSession()
      const expiresAt =
        typeof next?.expiresAt === 'number' && Number.isFinite(next.expiresAt)
          ? Math.round(next.expiresAt)
          : typeof next?.expiresIn === 'number' && Number.isFinite(next.expiresIn)
            ? Date.now() + Math.max(0, Math.round(next.expiresIn * 1000))
            : undefined
      setStoredTokens({ accessToken: next.accessToken, refreshToken: next.refreshToken || '', accessTokenExpiresAt: expiresAt })
      return next.accessToken
    } catch {}
    const next: any = await getCloudbaseTokens()
    const expiresAt =
      typeof next?.expiresAt === 'number' && Number.isFinite(next.expiresAt)
        ? Math.round(next.expiresAt)
        : typeof next?.expiresIn === 'number' && Number.isFinite(next.expiresIn)
          ? Date.now() + Math.max(0, Math.round(next.expiresIn * 1000))
          : undefined
    setStoredTokens({ accessToken: next.accessToken, refreshToken: next.refreshToken || '', accessTokenExpiresAt: expiresAt })
    return next.accessToken
  }

  async function tryRestoreLoginState() {
    const auth = await getCloudbaseAuth()
    const loginState = await auth.getLoginState()
    if (!loginState) return false
    await syncTokenFromCloudbase()
    return true
  }

  async function sendSmsCode(phoneNumber: string) {
    const auth = await getCloudbaseAuth()
    const verificationInfo = await auth.getVerification({ phone_number: phoneNumber })
    return verificationInfo
  }

  async function signInWithSms(phoneNumber: string, verificationCode: string, verificationInfo: any) {
    const auth = await getCloudbaseAuth()
    await auth.signInWithSms({
      verificationInfo,
      verificationCode,
      phoneNum: phoneNumber,
    })
    await syncTokenFromCloudbase()
  }

  async function sendPhoneBindCode(phoneNumber: string) {
    if (!token.value) throw new Error('未登录')
    const auth = await getCloudbaseAuth()
    const phone = String(phoneNumber || '').trim()
    try {
      const res = await (auth as any).updateUser({ phone })
      const err = (res as any)?.error
      if (err) throw err
      const data = (res as any)?.data
      if (!data || typeof (data as any).verifyOtp !== 'function') throw new Error('获取验证码失败')
      return data
    } catch (e) {
      const err = (e as any)?.error ?? (e as any)?.code
      const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
      const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
      const desc = String(descRaw || '').toLowerCase()
      const errStr = String(err || '').toLowerCase()
      const isPhoneAlreadyUsed = errCode === 9 || errStr === 'failed_precondition' || desc.includes('账号已存在')
      if (isPhoneAlreadyUsed) throw new Error('当前手机号已注册账号，请更换手机号')
      if (errCode === 3 || errStr === 'invalid_argument') {
        const hit =
          desc.includes('not_user') ||
          desc.includes('already') ||
          desc.includes('exist') ||
          desc.includes('registered') ||
          desc.includes('is_user') ||
          (desc.includes('user') && desc.includes('exist'))
        if (hit) throw new Error('当前手机号已注册账号，请更换手机号')
      }
      throw e
    }
  }

  async function bindPhoneNumber(phoneNumber: string, verificationCode: string, verificationInfo: any) {
    if (!token.value) throw new Error('未登录')
    const phone = String(phoneNumber || '').trim()
    const code = String(verificationCode || '').trim()
    const verifyOtp = (verificationInfo as any)?.verifyOtp
    if (typeof verifyOtp !== 'function') throw new Error('请先获取验证码')

    const callVerify = async (payload: any) => {
      const res = await verifyOtp.call(verificationInfo, payload)
      const anyRes = res as any
      const resErr =
        (anyRes && (anyRes.error || anyRes.error_code || anyRes.error_description || anyRes.code) ? anyRes : null) ||
        anyRes?.data?.error ||
        null
      if (resErr) throw resErr
      return res
    }

    try {
      try {
        await callVerify({ phone, token: code })
      } catch (e) {
        const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
        const desc = String(descRaw || '').toLowerCase()
        const err = (e as any)?.error ?? (e as any)?.code
        const errStr = String(err || '').toLowerCase()
        const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
        const isParamMismatch =
          errCode === 3 &&
          (errStr === 'invalid_argument' || errStr === 'invalid-argument') &&
          (desc.includes('phone') || desc.includes('手机号') || desc.includes('参数')) &&
          (desc.includes('missing') || desc.includes('required') || desc.includes('type') || desc.includes('format') || desc.includes('invalid'))
        if (!isParamMismatch) throw e
        await callVerify({ token: code })
      }
    } catch (e) {
      const err = (e as any)?.error ?? (e as any)?.code
      const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
      const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
      const desc = String(descRaw || '').toLowerCase()
      const errStr = String(err || '').toLowerCase()
      const isBadCode =
        errCode === 3 ||
        errStr === 'invalid_argument' ||
        desc.includes('验证码') ||
        (desc.includes('verification') && desc.includes('code')) ||
        desc.includes('invalid') ||
        desc.includes('incorrect') ||
        desc.includes('expired')
      if (isBadCode) throw new Error('验证码错误，请检查后重试。')
      throw e
    }

    await fetchMe()
    try {
      await fetchProviders()
    } catch {}
  }

  async function sendEmailCode(email: string) {
    const auth = await getCloudbaseAuth()
    const verificationInfo = await (auth as any).getVerification({ email: String(email || '').trim() })
    return verificationInfo
  }

  async function sendEmailBindCode(email: string) {
    if (!token.value) throw new Error('未登录')
    const auth = await getCloudbaseAuth()
    const em = String(email || '').trim()
    try {
      const res = await (auth as any).updateUser({ email: em })
      const err = (res as any)?.error
      if (err) throw err
      const data = (res as any)?.data
      if (!data || typeof (data as any).verifyOtp !== 'function') throw new Error('获取验证码失败')
      return data
    } catch (e) {
      const err = (e as any)?.error ?? (e as any)?.code
      const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
      const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
      const desc = String(descRaw || '').toLowerCase()
      const errStr = String(err || '').toLowerCase()
      const isEmailAlreadyUsed = errCode === 9 || errStr === 'failed_precondition' || desc.includes('账号已存在') || desc.includes('already exists')
      if (isEmailAlreadyUsed) throw new Error('当前邮箱已注册账号，请更换邮箱')
      if (errCode === 3 || errStr === 'invalid_argument') {
        const hit =
          desc.includes('not_user') ||
          desc.includes('already') ||
          desc.includes('exist') ||
          desc.includes('registered') ||
          desc.includes('is_user') ||
          (desc.includes('user') && desc.includes('exist'))
        if (hit) throw new Error('当前邮箱已注册账号，请更换邮箱')
      }
      throw e
    }
  }

  async function bindEmail(email: string, verificationCode: string, verificationInfo: any) {
    if (!token.value) throw new Error('未登录')
    const em = String(email || '').trim()
    const code = String(verificationCode || '').trim()
    const verifyOtp = (verificationInfo as any)?.verifyOtp
    if (typeof verifyOtp !== 'function') throw new Error('请先获取验证码')

    const callVerify = async (payload: any) => {
      const res = await verifyOtp.call(verificationInfo, payload)
      const anyRes = res as any
      const resErr =
        (anyRes && (anyRes.error || anyRes.error_code || anyRes.error_description || anyRes.code) ? anyRes : null) || anyRes?.data?.error || null
      if (resErr) throw resErr
      return res
    }

    try {
      try {
        await callVerify({ email: em, token: code })
      } catch (e) {
        const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
        const desc = String(descRaw || '').toLowerCase()
        const err = (e as any)?.error ?? (e as any)?.code
        const errStr = String(err || '').toLowerCase()
        const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
        const isParamMismatch =
          errCode === 3 &&
          (errStr === 'invalid_argument' || errStr === 'invalid-argument') &&
          (desc.includes('email') || desc.includes('邮箱') || desc.includes('参数')) &&
          (desc.includes('missing') || desc.includes('required') || desc.includes('type') || desc.includes('format') || desc.includes('invalid'))
        if (!isParamMismatch) throw e
        await callVerify({ token: code })
      }
    } catch (e) {
      const err = (e as any)?.error ?? (e as any)?.code
      const errCode = (e as any)?.error_code ?? (e as any)?.errorCode
      const descRaw = (e as any)?.error_description ?? (e as any)?.message ?? e
      const desc = String(descRaw || '').toLowerCase()
      const errStr = String(err || '').toLowerCase()
      const isBadCode =
        errCode === 3 ||
        errStr === 'invalid_argument' ||
        desc.includes('验证码') ||
        (desc.includes('verification') && desc.includes('code')) ||
        desc.includes('invalid') ||
        desc.includes('incorrect') ||
        desc.includes('expired')
      if (isBadCode) throw new Error('验证码错误，请检查后重试。')
      throw e
    }

    await fetchMe()
    try {
      await fetchProviders()
    } catch {}
  }

  async function signInWithEmail(email: string, verificationCode: string, verificationInfo: any) {
    const auth = await getCloudbaseAuth()
    if (typeof (auth as any).signInWithEmail === 'function') {
      await (auth as any).signInWithEmail({
        verificationInfo,
        verificationCode: String(verificationCode || '').trim(),
        email: String(email || '').trim(),
      })
      await syncTokenFromCloudbase()
      return
    }
    if (typeof (auth as any).signInWithOtp === 'function') {
      const res = await (auth as any).signInWithOtp({ email: String(email || '').trim() })
      const data = (res as any)?.data
      const err = (res as any)?.error
      if (err) throw err
      if (!data || typeof data.verifyOtp !== 'function') throw new Error('邮箱登录不可用')
      const v = await data.verifyOtp({ token: String(verificationCode || '').trim() })
      const vErr = (v as any)?.error
      if (vErr) throw vErr
      await syncTokenFromCloudbase()
      return
    }
    throw new Error('邮箱登录不可用')
  }

  async function startWeChatRedirectLogin(appid: string, scope: string) {
    const auth = await getCloudbaseAuth()
    const provider = auth.weixinAuthProvider({ appid, scope })
    provider.signInWithRedirect()
  }

  async function handleWeChatRedirectResult(appid: string, scope: string) {
    const auth = await getCloudbaseAuth()
    const provider = auth.weixinAuthProvider({ appid, scope })
    const loginState = await provider.getRedirectResult()
    if (!loginState) return false
    await syncTokenFromCloudbase()
    return true
  }

  async function genWeChatOpenQrUri(providerRedirectUri: string, state: string) {
    const auth = await getCloudbaseAuth()
    const res = await auth.genProviderRedirectUri({
      provider_id: 'wx_open',
      provider_redirect_uri: providerRedirectUri,
      state,
    })
    const uri = String((res as any)?.uri || '').trim()
    if (!uri) throw new Error('获取微信二维码失败')
    return uri
  }

  async function signInWithWeChatOpenCode(providerRedirectUri: string, providerCode: string) {
    const auth = await getCloudbaseAuth()
    const { provider_token } = await auth.grantProviderToken({
      provider_id: 'wx_open',
      provider_redirect_uri: providerRedirectUri,
      provider_code: providerCode,
    })
    if (!provider_token) throw new Error('微信登录失败')
    
    const isHttp404 = (e: any) => {
      const status = (e as any)?.status
      const code = (e as any)?.code
      const msg = String((e as any)?.message || e || '').toLowerCase()
      return status === 404 || code === 404 || msg.includes('status code 404') || msg.includes(' 404 ')
    }
    const getAuthErr = (e: any) => {
      const error = (e as any)?.error ?? (e as any)?.code
      const errorCode = (e as any)?.error_code ?? (e as any)?.errorCode
      const errorDesc = String((e as any)?.error_description || (e as any)?.message || '').trim()
      const status = (e as any)?.status
      return { error, errorCode, errorDesc, status }
    }
    const setWxOpenPendingProviderToken = (t: string) => {
      try {
        window.sessionStorage.setItem(WX_OPEN_PENDING_PROVIDER_TOKEN_KEY, String(t || ''))
      } catch {}
    }
    const shouldFallbackToV1 = (e: any) => {
      if (isHttp404(e)) return true
      const { error, errorDesc, status } = getAuthErr(e)
      if (status === 404) return true
      if (error !== 'not_found') return false
      const d = errorDesc.toLowerCase()
      return d.includes('provider') || d.includes('endpoint') || d.includes('profile') || d.includes('用户名') || d.includes('密码')
    }
    try {
      try {
        await auth.signInWithProvider({ provider_token, provider_id: 'wx_open', version: 'v2' } as any)
      } catch (e) {
        if (!shouldFallbackToV1(e)) throw e
        await auth.signInWithProvider({ provider_token, provider_id: 'wx_open', version: 'v1' } as any)
      }
    } catch (e) {
      const { error, errorDesc } = getAuthErr(e)
      if (error !== 'not_found') throw e
      setWxOpenPendingProviderToken(provider_token)
      throw new Error(errorDesc || '微信账号未绑定：请先使用短信验证码登录/注册后自动绑定微信')
    }
    await syncTokenFromCloudbase()
  }

  async function bindWeChatOpenCode(providerRedirectUri: string, providerCode: string) {
    if (!token.value) throw new Error('未登录')
    const auth = await getCloudbaseAuth()
    const { provider_token } = await auth.grantProviderToken({
      provider_id: 'wx_open',
      provider_redirect_uri: providerRedirectUri,
      provider_code: providerCode,
    })
    if (!provider_token) throw new Error('微信绑定失败')
    await auth.bindWithProvider({ provider_token })
    await fetchMe()
    try {
      await fetchProviders()
    } catch {}
  }

  async function unbindWeChatOpenProvider() {
    if (!token.value) throw new Error('未登录')
    const auth = await getCloudbaseAuth()
    const provider_id = wxOpenProfile.value?.provider_id || 'wx_open'
    await auth.unbindProvider({ provider_id } as any)
    await fetchMe()
    try {
      await fetchProviders()
    } catch {}
  }

  async function updateProfile(nickName?: string, avatarUrl?: string) {
    if (!token.value) throw new Error('未登录')
    const nn = nickName === undefined ? undefined : String(nickName || '').trim().slice(0, 32)
    const au = avatarUrl === undefined ? undefined : normalizeAvatarValue(String(avatarUrl || '').trim())
    const payload: any = {}
    if (nn !== undefined) payload.nickname = nn
    if (au !== undefined) payload.avatar_url = au
    if (!Object.keys(payload).length) return
    const cbAuth = await getCloudbaseAuth()
    try {
      const res = await (cbAuth as any).updateUser(payload)
      const err = (res as any)?.error
      if (err) throw new Error(String((err as any)?.message || (err as any)?.error_description || (err as any)?.error || '更新失败'))
    } catch (e) {
      await (cbAuth as any).updateUserBasicInfo(payload)
    }
    me.value = {
      ...(me.value || {}),
      ...(nn !== undefined ? { nick_name: nn } : null),
      ...(au !== undefined ? { avatar_url: au } : null),
    }
    try {
      await fetchMe()
      if (nn !== undefined || au !== undefined) {
        me.value = {
          ...(me.value || {}),
          ...(nn !== undefined ? { nick_name: nn } : null),
          ...(au !== undefined ? { avatar_url: au } : null),
        }
      }
    } catch {}
  }

  async function bindPendingWeChatOpenProvider() {
    const t = (() => {
      try {
        const v = String(window.sessionStorage.getItem(WX_OPEN_PENDING_PROVIDER_TOKEN_KEY) || '').trim()
        window.sessionStorage.removeItem(WX_OPEN_PENDING_PROVIDER_TOKEN_KEY)
        return v
      } catch {
        return ''
      }
    })()
    if (!t) return false
    const auth = await getCloudbaseAuth()
    try {
      await auth.bindWithProvider({ provider_token: t })
    } catch (bindErr) {
      console.error('[wx-login] bindWithProvider failed', bindErr)
      throw bindErr
    }
    return true
  }

  async function signOut() {
    const auth = await getCloudbaseAuth()
    await auth.signOut()
    clearToken()
  }

  return {
    token,
    refreshToken,
    tokenExpiresAt,
    me,
    providers,
    isLoggedIn,
    wxOpenProfile,
    syncFromStorage,
    setToken,
    clearToken,
    fetchMe,
    fetchProviders,
    refreshAccessToken,
    tryRestoreLoginState,
    sendSmsCode,
    signInWithSms,
    sendPhoneBindCode,
    bindPhoneNumber,
    sendEmailCode,
    sendEmailBindCode,
    bindEmail,
    signInWithEmail,
    startWeChatRedirectLogin,
    handleWeChatRedirectResult,
    genWeChatOpenQrUri,
    signInWithWeChatOpenCode,
    bindWeChatOpenCode,
    unbindWeChatOpenProvider,
    updateProfile,
    bindPendingWeChatOpenProvider,
    signOut,
  }
})
